// keyrd_console/src/components/PilotAuditTrail.js
import React, { useEffect, useMemo, useState } from "react";
import { getPilotAuditTrail } from "../api/pilotAdmin";
import PilotGovernanceBanner from "./PilotGovernanceBanner";
import PilotStatusPill from "./PilotStatusPill";
import PilotDataSourceBadge from "./PilotDataSourceBadge";

const pageStyle = {
  padding: 20,
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const titleRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 12,
};

const titleStyle = {
  fontSize: 24,
  fontWeight: 700,
  color: "#0f172a",
  margin: 0,
};

const subtitleStyle = {
  color: "#475569",
  fontSize: 14,
  marginTop: 4,
};

const controlRowStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
};

const inputStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  minWidth: 260,
};

const selectStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
};

const listStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const itemStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  cursor: "pointer",
};

const itemTopStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  flexWrap: "wrap",
};

const itemTitleStyle = {
  fontSize: 16,
  fontWeight: 700,
  color: "#0f172a",
};

const metaTextStyle = {
  fontSize: 13,
  color: "#64748b",
};

const rowWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

const loadingStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#334155",
};

const errorStyle = {
  padding: 14,
  borderRadius: 10,
  background: "#fef2f2",
  color: "#991b1b",
  border: "1px solid #fecaca",
};

const emptyStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#475569",
};

function normalizeRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.audit_rows)) return payload.audit_rows;
  if (Array.isArray(payload?.events)) return payload.events;
  return [];
}

function matchesQuery(row, query) {
  if (!query) return true;
  const haystack = [
    row.participant_uuid,
    row.operator_id,
    row.event_type,
    row.reason_code,
    row.severity,
    row.evidence_state,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(query.toLowerCase());
}

function filterRows(rows, query, severity) {
  return rows.filter((row) => {
    const queryOk = matchesQuery(row, query);
    const severityOk =
      severity === "all" || String(row.severity || "").toLowerCase() === severity;
    return queryOk && severityOk;
  });
}

function PilotAuditTrail({ auth, onSelectParticipant }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [query, setQuery] = useState("");
  const [severity, setSeverity] = useState("all");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await getPilotAuditTrail(auth);
        if (!cancelled) {
          setPayload(res?.data || res || {});
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load pilot audit trail.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const data = payload || {};
  const rows = normalizeRows(data);
  const filteredRows = useMemo(
    () => filterRows(rows, query, severity),
    [rows, query, severity]
  );

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={loadingStyle}>Loading pilot audit trail…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={errorStyle}>{error}</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={titleRowStyle}>
        <div>
          <h1 style={titleStyle}>Audit Trail</h1>
          <div style={subtitleStyle}>
            Operator-visible record of review actions, severity, reasons, timestamps, and evidence context.
          </div>
        </div>
        <PilotDataSourceBadge source={data.data_source} stub={Boolean(data.stub)} />
      </div>

      <PilotGovernanceBanner auth={auth} system={data.system || data} />

      <div style={controlRowStyle}>
        <input
          style={inputStyle}
          type="text"
          value={query}
          placeholder="Search participant, operator, reason, event…"
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          style={selectStyle}
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
        >
          <option value="all">All severities</option>
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
      </div>

      {!filteredRows.length ? (
        <div style={emptyStyle}>No audit events matched the current filters.</div>
      ) : (
        <div style={listStyle}>
          {filteredRows.map((row, idx) => (
            <div
              key={`${row.timestamp_utc || "na"}-${row.participant_uuid || "na"}-${idx}`}
              style={itemStyle}
              onClick={() =>
                typeof onSelectParticipant === "function" &&
                row.participant_uuid &&
                onSelectParticipant(row.participant_uuid)
              }
            >
              <div style={itemTopStyle}>
                <div>
                  <div style={itemTitleStyle}>{row.event_type || "audit_event"}</div>
                  <div style={metaTextStyle}>
                    Participant: {row.participant_uuid || "n/a"} · Operator:{" "}
                    {row.operator_id || "unknown"}
                  </div>
                </div>
                <PilotStatusPill
                  status={row.severity || "unknown"}
                  label={row.severity || "unknown"}
                />
              </div>

              <div style={rowWrapStyle}>
                <PilotStatusPill
                  status={row.reason_code || "unknown"}
                  label={`Reason: ${row.reason_code || "unknown"}`}
                />
                <PilotStatusPill
                  status={row.evidence_confidence || "unknown"}
                  label={`Evidence: ${row.evidence_confidence || "unknown"}`}
                />
              </div>

              <div style={metaTextStyle}>
                Evidence State: {row.evidence_state || "unknown"}
              </div>
              <div style={metaTextStyle}>
                Timestamp: {row.timestamp_utc || "unknown"}
              </div>
              {row.review_comment ? (
                <div style={metaTextStyle}>Comment: {row.review_comment}</div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PilotAuditTrail;